// Story 1.10 / FR-3, FR-19: model settings и безопасная storage-диагностика.
//
// Модуль держит всю HTTP/pure-логику панели System:
// * draft → safe payload (credential передаётся только непустым, write-only);
// * клиентская pre-validation повторяет серверные границы (сервер всё равно
//   проверяет сам и его отказ не доверяется клиентским status/dimension);
// * readiness contract → строки диагностики (только configured components);
// * mapping safe reason → locale key без вывода raw body/URL.
//
// Значение credential никогда не сохраняется в state и не попадает в текст.
import { apiFetch } from "./api.js";

export const MODEL_SETTINGS_ENDPOINT = "/api/system/models";
export const MODEL_SETTINGS_TEST_ENDPOINT = "/api/system/models/test";
export const READINESS_ENDPOINT = "/readiness";

// Story 1.11: selected graph backend is one row — `age` (default) or `memgraph`
// (legacy explicit) on success, `graph` for an unsupported selector. Only rows
// actually present in the readiness payload are rendered; the UI never invents
// a missing backend and never shows two graph rows.
export const STORAGE_COMPONENTS = ["postgres", "qdrant", "age", "memgraph", "graph", "s3"];

// Allowlisted safe reasons from the readiness contract (DECISION §6). Anything
// else falls back to the generic reason text; raw payload values never render.
export const STORAGE_REASONS = Object.freeze([
  "graph_backend_unsupported",
  "graph_backend_incompatible",
  "configuration_missing",
  "authentication_failed",
  "connection_failed",
  "dependency_error",
  "timeout",
]);

/** `age`/`memgraph`/`graph` (invalid config) → static locale key. */
export function storageComponentKey(component) {
  const value = typeof component === "string" ? component : "";
  return STORAGE_COMPONENTS.includes(value)
    ? `system.storage.component.${value}`
    : "system.storage.component.graph";
}

// Story 1.14: allowlisted next-action vocabulary из ReadinessContract
// (`ReadinessAction`). Отдельный список от reason allowlist: action и reason
// не смешиваются, raw значение никогда не рендерится.
export const STORAGE_ACTIONS = Object.freeze([
  "check_configuration",
  "check_credentials",
  "check_connection",
  "check_component",
  "retry_check",
]);

/** Allowlisted reason → static locale key; unknown reasons stay generic. */
export function storageReasonKey(reason) {
  const value = typeof reason === "string" ? reason : "";
  return STORAGE_REASONS.includes(value)
    ? `system.storage.reason.${value}`
    : "system.storage.reason.unknown";
}

/** Allowlisted readiness action → static locale key; anything else is generic. */
export function storageActionKey(action) {
  const value = typeof action === "string" ? action : "";
  return STORAGE_ACTIONS.includes(value)
    ? `system.storage.action.${value}`
    : "system.storage.action.unknown";
}

/** Ready/not_ready status → static locale key. */
export function storageStatusKey(status) {
  return status === "ready" ? "system.storage.status.ready" : "system.storage.status.notReady";
}
export const MAX_DECLARED_DIMENSION = 65536;

const MODEL_REASONS = new Set([
  "invalid_base_url",
  "invalid_model",
  "invalid_dimension",
  "invalid_credential",
  "timeout",
  "unsupported_model",
  "connection_failed",
  "dependency_error",
  "response_too_large",
  "invalid_response",
  "count_mismatch",
  "dimension_mismatch",
  "non_finite",
  "zero_vector",
  "model_mismatch",
  "successful_connection_test_required",
  "draft_changed_after_test",
  "revision_mismatch",
  "probe_revision_mismatch",
  "model_settings_storage_unavailable",
  "model_probe_concurrency_exhausted",
  "request_too_large",
  "invalid_request_body",
  "authentication_required",
]);

export function modelReasonKey(reason) {
  const value = typeof reason === "string" ? reason : "";
  return MODEL_REASONS.has(value) ? `system.model.reason.${value}` : "system.model.reason.unknown";
}

/** Значение credential — только write-only: в state не сохраняется. */
export function modelDraft(overrides = {}) {
  return {
    provider: "local",
    base_url: "",
    model: "",
    declared_dimension: "",
    credential: "",
    ...overrides,
  };
}

export function modelDraftFromSettings(settings) {
  const payload = settings && typeof settings === "object" ? settings : {};
  const dimension =
    payload.declared_dimension === null || payload.declared_dimension === undefined
      ? ""
      : String(payload.declared_dimension);
  return modelDraft({
    provider: payload.provider === "remote" ? "remote" : "local",
    base_url: String(payload.base_url || ""),
    model: String(payload.model || ""),
    declared_dimension: dimension,
  });
}

function normalizedDimension(value) {
  const raw = String(value ?? "").trim();
  if (!/^\d+$/.test(raw)) return NaN;
  return Number(raw);
}

/** Клиентская проверка границ; сервер остаётся единственным authority. */
export function validateModelDraft(draft) {
  const value = draft || {};
  const baseUrl = String(value.base_url || "").trim();
  let parsed;
  try {
    parsed = new URL(baseUrl);
  } catch (_error) {
    return { field: "base_url", reason: "invalid_base_url" };
  }
  if (
    !/^https?:$/.test(parsed.protocol) ||
    !parsed.hostname ||
    parsed.username ||
    parsed.password ||
    parsed.search ||
    parsed.hash ||
    (parsed.pathname !== "" && parsed.pathname !== "/")
  ) {
    return { field: "base_url", reason: "invalid_base_url" };
  }
  const model = String(value.model || "").trim();
  if (!model || model.length > 512 || /[\u0000-\u001f\u007f]/.test(model)) {
    return { field: "model", reason: "invalid_model" };
  }
  const dimension = normalizedDimension(value.declared_dimension);
  if (!Number.isInteger(dimension) || dimension < 1 || dimension > MAX_DECLARED_DIMENSION) {
    return { field: "declared_dimension", reason: "invalid_dimension" };
  }
  const credential = String(value.credential ?? "");
  if (credential && (credential.length > 8192 || /[\u0000-\u001f\u007f]/.test(credential))) {
    return { field: "credential", reason: "invalid_credential" };
  }
  return null;
}

export function modelTestPayload(draft, revision) {
  const value = draft || {};
  const credential = String(value.credential ?? "");
  return {
    expected_revision: Number(revision) || 0,
    provider: value.provider === "remote" ? "remote" : "local",
    base_url: String(value.base_url || "").trim(),
    model: String(value.model || "").trim(),
    declared_dimension: normalizedDimension(value.declared_dimension),
    ...(credential ? { credential } : {}),
  };
}

export function modelSavePayload(draft, revision, probeId) {
  return {
    ...modelTestPayload(draft, revision),
    probe_id: String(probeId || ""),
  };
}

export function modelProbeAccepted(response) {
  return Boolean(
    response &&
      typeof response === "object" &&
      response.status === "ok" &&
      typeof response.probe_id === "string" &&
      response.probe_id !== "",
  );
}

export function modelActualDimension(response) {
  const value = response && response.actual_dimension;
  return Number.isInteger(value) && value > 0 ? value : null;
}

/** Причина отказа: только safe словарь, никогда raw message/body. */
export function modelRefusalReason(outcome) {
  const payload = outcome && outcome.payload && typeof outcome.payload === "object" ? outcome.payload : {};
  const reason = typeof payload.reason === "string" ? payload.reason : "";
  if (reason) return reason;
  if (outcome && outcome.status === 409) return "revision_mismatch";
  if (outcome && outcome.status === 401) return "authentication_required";
  return "unknown";
}

export function modelSettingsErrorKey(outcome) {
  return modelReasonKey(modelRefusalReason(outcome));
}

/** Read-only диагностика: только configured components из readiness contract. */
export function readinessRows(contract) {
  const checks = contract && Array.isArray(contract.checks) ? contract.checks : [];
  return STORAGE_COMPONENTS.flatMap((component) => {
    const check = checks.find((entry) => entry && entry.name === component);
    if (!check) return [];
    return [
      {
        component,
        status: check.status === "ready" ? "ready" : "not_ready",
        reason: typeof check.reason === "string" ? check.reason : "",
        action: typeof check.action === "string" ? check.action : "",
      },
    ];
  });
}

// Story 1.14: валидный readiness-контракт требует обязательных компонентов
// (postgres/qdrant/s3) и ровно одной graph-строки; aggregate status обязан
// совпадать со строками. Любое расхождение — unknown, а не ready: malformed
// тело не должно открывать upload.
export const READINESS_REQUIRED_COMPONENTS = Object.freeze(["postgres", "qdrant", "s3"]);
export const READINESS_GRAPH_COMPONENTS = Object.freeze(["age", "memgraph", "graph"]);

export function readinessStatus(contract) {
  if (!contract || typeof contract !== "object" || !Array.isArray(contract.checks)) {
    return "unknown";
  }
  if (contract.status !== "ready" && contract.status !== "not_ready") {
    return "unknown";
  }
  if (contract.checks.length === 0) return "unknown";
  const named = new Set();
  let notReady = 0;
  for (const check of contract.checks) {
    if (!check || typeof check !== "object") return "unknown";
    if (typeof check.name !== "string" || !check.name) return "unknown";
    if (check.status !== "ready" && check.status !== "not_ready") return "unknown";
    if (named.has(check.name)) return "unknown";
    named.add(check.name);
    if (check.status === "not_ready") notReady += 1;
  }
  if (!READINESS_REQUIRED_COMPONENTS.every((name) => named.has(name))) return "unknown";
  const graphRows = READINESS_GRAPH_COMPONENTS.filter((name) => named.has(name));
  // Реальный backend всегда включает ровно одну graph-строку (age|memgraph|graph):
  // её отсутствие — incomplete payload, а не готовая система.
  if (graphRows.length !== 1) return "unknown";
  // Противоречивый aggregate: ready с failed-строкой или not_ready без неё.
  if (contract.status === "ready" && notReady > 0) return "unknown";
  if (contract.status === "not_ready" && notReady === 0) return "unknown";
  return contract.status;
}

async function readOutcome(response) {
  let payload = null;
  try {
    payload = await response.json();
  } catch (_error) {
    payload = null;
  }
  return { ok: response.ok, status: response.status, payload };
}

export async function loadModelSettings() {
  const response = await apiFetch(MODEL_SETTINGS_ENDPOINT, {
    headers: { Accept: "application/json" },
  });
  return readOutcome(response);
}

export async function testModelSettings(payload) {
  const response = await apiFetch(MODEL_SETTINGS_TEST_ENDPOINT, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return readOutcome(response);
}

export async function saveModelSettings(payload) {
  const response = await apiFetch(MODEL_SETTINGS_ENDPOINT, {
    method: "PUT",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return readOutcome(response);
}

export async function loadReadiness(options = {}) {
  const response = await apiFetch(READINESS_ENDPOINT, {
    headers: { Accept: "application/json" },
    ...(options.signal ? { signal: options.signal } : {}),
  });
  return readOutcome(response);
}
